import React, {Component} from 'react';
import styled from 'styled-components';
import queryString from 'query-string';
import {NotificationManager} from 'react-notifications';
import {T} from '../components/I18n';

import * as api from '../lib/api';
import withUrlState from '../lib/with-url-state';
import {withUser} from '../lib/user';
import search from '../lib/search';
import {alphaSort} from '../lib/sort';
import ListScreen from '../components/ListScreen';
import ListItem from '../components/ListItem';
import Loading from '../components/Loading';
import Error from '../components/Error';
import Tabs from '../components/Tabs';
import Input from '../components/Input';
import {Tag, AddTagButton, TagsContainer} from '../components/TagComponents';
import ReportParametersModal from '../components/ReportParametersModal';
import TagManagementModal from '../components/TagManagementModal';
import Screen from '../components/Screen';
import SubHeader from '../components/SubHeader';
import Content from '../components/Content';
import Role from "../lib/Role";
import Icon from '../components/Icon';
import * as tagHelpers from '../lib/tag-helpers';


class ReportScreen extends Component {

  state = {
    loading: true,
    error: false,
    reports: [],
    catalog: [],
    activeReport: null,
    tagManagementReport: null,
    tagUpdateCounter: 0,
    availableTags: []
  };

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [reports, catalog] = await Promise.all([
        api.getReports(credentials),
        api.getCatalog(credentials)
      ]);
      
      let availableTags = [];
      try {
        availableTags = await tagHelpers.fetchAvailableTags(credentials, 'form');
      } catch (error) {
        availableTags = tagHelpers.extractTagsFromItems(reports);
      }

      this.setState({reports, catalog, availableTags});
    }
    catch (e) {
      this.setState({error: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  async handlePublish(report) {
    const {credentials} = this.props;
    
    // eslint-disable-next-line
    if (!confirm('Are you sure you want to publish this report? No changes can be made once published.')) {
      return;
    }

    try {
      this.setState(state => ({
        reports: state.reports.map(r => {
          if(r.id === report.id) {
            return {...report, published: true};
          }
  
          return r;
        })
      }));

      await api.publishReport(report.id, credentials);
      NotificationManager.success(`${report.name} was published`);
    }
    catch (e) {
      NotificationManager.error('Something went wrong while publishing this report');
    }
  }
  
  handleSearch = e => {
    this.props.setUrlState({q: e.target.value});
  }

  handleSortChange = e => {
    this.props.setUrlState({sort: e.target.value});
  }

  handleTypeChange = e => {
    this.props.setUrlState({type: e.target.value});
  }

  handleTagFilterChange = e => {
    this.props.setUrlState({tag: e.target.value});
  }

  handleReportParameters = report => {
    this.setState({activeReport: report});
  }

  handleCancelParameters = () => {
    this.setState({activeReport: null});
  }

  handleRunReport = (report, parameters) => {
    const {history} = this.props;
    history.push(`/reports/${report.id}?${queryString.stringify(parameters)}`);
  }

  handleOpenTagManagement = report => {
    this.setState({tagManagementReport: report});
  }

  handleCloseTagManagement = () => {
    this.setState({tagManagementReport: null});
  }


  handleSaveTags = async (report, selectedTags, allAvailableTags, tagsToDelete = []) => {
    const {reports} = this.state;
    const {credentials} = this.props;
    
    const currentTagNames = this.state.availableTags.map(tagHelpers.getTagName);
    const result = await tagHelpers.saveTags(report, selectedTags, allAvailableTags, tagsToDelete, credentials, 'form', currentTagNames, 'report');
    
    if (result.success) {
      const updatedReports = tagHelpers.updateItemsAfterTagChanges(reports, report, selectedTags, tagsToDelete);
      const newAvailableTags = await tagHelpers.fetchAvailableTags(credentials, 'form');
      this.setState({
        reports: updatedReports,
        availableTags: newAvailableTags,
        tagManagementReport: null,
        tagUpdateCounter: this.state.tagUpdateCounter + 1
      });
    }
  }

  render() {
    const {user} = this.props;
    const {reports, catalog, loading, error, activeReport, tagManagementReport} = this.state;

    // TODO check the feature flags for this company instead of checking number of reports.
    if (reports.length < 1 && !loading && !error) {
      return (
        <Screen title="Reports" activeLink="Reports" showTitle={false}>
          <Content>
            <NoReports>
              <Message>
                <img src="/assets/reporting.svg" alt="" width="290" />
                <SubHeader center>You have no reports</SubHeader>
                <p>Contact your account manager to learn more about custom reports.</p>
              </Message>
            </NoReports>
          </Content>
        </Screen>
      );
    }

    const {sort, type, q, tag} = this.props.urlState;
    const sortedAndFilteredReports = reports
      .filter(filterByType(type))
      .filter(report => search(report.name, q))
      .filter(tagHelpers.createTagFilter(tag))
      .sort(alphaSort(sort, report => report.name));
    
    const listKey = `${type}-${tag || 'all'}-${sort}-${this.state.tagUpdateCounter}`;

    return (
      <>
        <ListScreen
          key={listKey}
          title="Reports"
          activeLink="Reports"
          loading={loading && <Loading fullScreen />}
          error={error && <Error fullScreen />}
          items={sortedAndFilteredReports}
          empty={<>There are no reports to display.</>}
          search={q}
          onSearch={this.handleSearch}
          primaryAction={null}
          mobileActions={null}
          toolbarActions={[
            <Input id="form-sort" label="Sort by:" type="select" value={sort} onChange={this.handleSortChange}>
              <option value="asc">Alphabetical</option>
              <option value="desc">Reverse Alphabetical</option>
            </Input>,
            <Input id="tag-filter" label="Tag:" type="select" value={tag || 'all'} onChange={this.handleTagFilterChange} style={{maxWidth: '200px', minWidth: '120px'}}>
              <option value="all">All Tags</option>
              {this.state.availableTags.map(tagObj => {
                const tagName = typeof tagObj === 'string' ? tagObj : tagObj.name;
                return (
                  <option key={tagName} value={tagName} title={tagName}>
                    {tagName.length > 30 ? tagName.substring(0, 30) + '...' : tagName}
                  </option>
                );
              })}
            </Input>,
            user.role === Role.ADMIN && (
              <Input id="form-type-filter" label="Filter:" type="select" value={type} onChange={this.handleTypeChange}>
                <option value="published">Published Reports</option>
                <option value="unpublished">Unpublished Reports</option>
              </Input>
            )
          ].filter(Boolean)}
          tabs={
            <Tabs>
              <Tabs.Tab active to="/reports">Reports</Tabs.Tab>
              <Tabs.Tab to="/reports/schedules">Schedules</Tabs.Tab>
            </Tabs>
          }>
          {(report, i) => (
            <ListItem
              key={`${report.id}-${report.tags ? report.tags.length : 0}-${JSON.stringify(report.tags)}`}
              first={i === 0}
              options={[
                {
                  title: 'Run',
                  [report.parameters.length > 0 ? 'onClick' : 'to']: report.parameters.length > 0
                    ? () => this.handleReportParameters(report)
                    : `/reports/${report.id}`
                },
                report.published
                  ? {title: 'View Schedules', to: `/reports/schedules?report=${report.id}`}
                  : {title: 'Publish', onClick: () => this.handlePublish(report)},
                {title: 'Modify Tags', onClick: () => this.handleOpenTagManagement(report)}
              ].filter(Boolean)}
            >
              <ReportItemContent>
                <strong><T noTranslate>{report.name}</T></strong>
                <ReportSubtitle>
                  <span>{report.description}</span>
                  <TagsContainer variant="form" hasContent={!!(report.tags && report.tags.length > 0)}>
                    {report.tags && report.tags.length > 0 && report.tags.map(tag => {
                      const tagName = typeof tag === 'string' ? tag : tag.name;
                      return (
                        <Tag key={tagName} onClick={() => this.handleTagFilterChange({target: {value: tagName}})}>{tagName}</Tag>
                      );
                    })}
                    {report.tags && report.tags.length > 0 && (
                      <AddTagButton onClick={() => this.handleOpenTagManagement(report)}>
                        <Icon name="plus" size="1em" />
                      </AddTagButton>
                    )}
                  </TagsContainer>
                </ReportSubtitle>
              </ReportItemContent>
            </ListItem>
          )}
        </ListScreen>
        <ReportParametersModal
          catalog={catalog}
          report={activeReport}
          parameters={getDefaultParameters(activeReport)}
          onRun={this.handleRunReport}
          onClose={this.handleCancelParameters}
        />
        <TagManagementModal
          open={!!tagManagementReport}
          onClose={this.handleCloseTagManagement}
          item={tagHelpers.normalizeItemForEditing(tagManagementReport)}
          itemName={tagManagementReport && tagManagementReport.name}
          availableTags={this.state.availableTags}
          entityType="form"
          onSave={this.handleSaveTags}
        />
      </>
    );
  }
}

function filterByType(type) {
  return report => type === 'published' ? report.published : !report.published;
}

function getDefaultParameters(report){
  if (!report)
    return {};

  return report.parameters.reduce((params, param) => {
    if (param.defaultValue) {
      switch (true) {
        case param.dataType === 4 :
        case param.dataType === 7 :
          params[param.name] = Date.parse(param.defaultValue).toString();
          break;

        default:
          params[param.name] = param.defaultValue;
      }
    }
    return params;
  }, {});
}

const NoReports = styled.div`
  height: calc(100vh - 2rem);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Message = styled.div`
  img {
    display: block;
    margin: 0 auto 2rem auto;
  }

  p {
    color: #747474;
    margin-top: 1rem;
    line-height: 1.5;
  }
`;

const defaultUrlState = {
  sort: 'asc',
  type: 'published',
  q: '',
  tag: 'all'
};

const ReportItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  
  strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ReportSubtitle = styled.small`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #666;
  
  span {
    display: block;
  }
`;

export default withUrlState(defaultUrlState)(withUser(ReportScreen));
